import React from 'react';
import PropTypes from 'prop-types';
import { Mutation, MutationComponentOptions } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import Styled from 'styled';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import others from 'four-nets/icons/svg/icon_others.svg';
import comments from 'four-nets/icons/svg/icon_comment.svg';

import ComplainDialog from 'four-nets/ui-v2/complain-dialog/complain-dialog';
import { AlertContext } from 'lib/alert-context/alert-context';
import ActionSheet, { Action } from 'four-nets/ui-v2/action-sheet/action-sheet';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import { UserStore } from 'four-nets/user/user';
import Spinner from 'four-nets/ui/spinner/spinner';

import SubscribeUnsubscribePost from './subscribe-unsubscribe-post';
import Button from './buttons/button/button';
import Likers from './buttons/post-likers/post-likers-button';
import LikeUnlike from './buttons/like-unlike/like-unlike';

import { subscribePostMutation, subscribePostMutationVariables } from './mutations/subscribe.graphql';
import { unsubscribePostMutation, unsubscribePostMutationVariables } from './mutations/unsubscribe.graphql';
import complainPostMutation, {
    ComplainPostMutation,
    ComplainPostMutationVariables,
} from './mutations/complain-post.graphql';
import removePostMutation, { RemovePostMutation, RemovePostMutationVariables } from './mutations/remove-post.graphql';

import { PostType } from './footer.graphql.types';
import { FooterFragment } from './footer.graphql';

import defaultStyles from './footer.scss';

export interface Props extends FooterFragment {
    styles: typeof defaultStyles;
    onComments?: () => void;
    refetch?: () => void;
    isPrArticle?: boolean;
    commentsLoading?: boolean;
    commentsOpen?: boolean;
    inFeed?: boolean;
}

interface State {
    showMore: boolean;
}

type User = FooterFragment['user'];
type Viewer = UserStore['me'] | null;

class Footer extends React.PureComponent<Props, State> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };

    state: State = {
        showMore: false,
    };

    openMore = () => {
        this.setState({
            showMore: true,
        });
    };

    closeMore = () => {
        this.setState({
            showMore: false,
        });
    };

    permissionToDelete = (user: User, viewer: Viewer, postType: PostType | null) => {
        if (viewer && viewer.isSuperstaff) {
            return true;
        }
        if (viewer && user) {
            return user.username === viewer.username && postType === 'POST_SHORT_MESSAGE';
        }
        return false;
    };

    render() {
        const {
            id,
            refetch,
            styles,
            user,
            postType,
            isLikedByViewer,
            isSubscribedByViewer,
            likesCount,
            commentCount,
            onComments,
            __typename,
            isPrArticle,
            commentsLoading,
            commentsOpen,
            inFeed,
            ...rest
        } = this.props;

        return (
            <AlertContext.Consumer>
                {({ confirm }) => (
                    <Mutation<RemovePostMutation, RemovePostMutationVariables> mutation={removePostMutation}>
                        {removePost => {
                            return (
                                <ComplainDialog>
                                    {({ complain: openComplain }) => (
                                        <SubscribeUnsubscribePost id={id} isSubscribed={Boolean(isSubscribedByViewer)}>
                                            {(
                                                subscribePost: MutationComponentOptions<
                                                    subscribePostMutation,
                                                    subscribePostMutationVariables
                                                >,
                                                unsubscribePost: MutationComponentOptions<
                                                    unsubscribePostMutation,
                                                    unsubscribePostMutationVariables
                                                >
                                            ) => (
                                                <Mutation<ComplainPostMutation, ComplainPostMutationVariables>
                                                    mutation={complainPostMutation}
                                                >
                                                    {complain => {
                                                        const onSubscribe = () =>
                                                            this.context.authCheck(() => {
                                                                this.closeMore();

                                                                if (isSubscribedByViewer) {
                                                                    return unsubscribePost({ variables: { id } });
                                                                } else {
                                                                    return subscribePost({ variables: { id } });
                                                                }
                                                            }, AuthOrigin.Notifications);

                                                        const onComplain = () =>
                                                            this.context.authCheck(() => {
                                                                this.closeMore();

                                                                openComplain({
                                                                    onSubmit: ({ comment }) =>
                                                                        complain({ variables: { id, comment } }),
                                                                });

                                                                return Promise.resolve();
                                                            }, AuthOrigin.NewComplain);

                                                        const onRemove = () =>
                                                            confirm({
                                                                body: (
                                                                    <span>
                                                                        <FormattedMessage
                                                                            id='ARE_YOU_SURE_YOU_WANT_TO_DELETE_SHORT_MESSAGE'
                                                                            values={{ gender: 'other' }}
                                                                        />
                                                                        ?
                                                                    </span>
                                                                ),
                                                                onAccept: () => {
                                                                    this.closeMore();

                                                                    removePost({ variables: { id } }).then(() => {
                                                                        if (refetch) {
                                                                            refetch();
                                                                        }
                                                                    });
                                                                },
                                                                acceptButtonType: 'warn',
                                                            });

                                                        return (
                                                            <>
                                                                <ActionSheet
                                                                    open={this.state.showMore}
                                                                    onClose={this.closeMore}
                                                                >
                                                                    {({ close }) => (
                                                                        <React.Fragment>
                                                                            <Action onClick={onSubscribe}>
                                                                                {isSubscribedByViewer ? (
                                                                                    <FormattedMessage id='DISABLE_NOTIFICATIONS' />
                                                                                ) : (
                                                                                    <FormattedMessage id='ENABLE_NOTIFICATIONS' />
                                                                                )}
                                                                            </Action>
                                                                            <Action onClick={onComplain}>
                                                                                <FormattedMessage id='REPORT' />
                                                                            </Action>
                                                                            <ViewerConsumer>
                                                                                {({ viewer }) =>
                                                                                    this.permissionToDelete(
                                                                                        user,
                                                                                        viewer,
                                                                                        postType
                                                                                    ) ? (
                                                                                        <Action onClick={onRemove}>
                                                                                            <FormattedMessage
                                                                                                id='DELETE'
                                                                                                values={{
                                                                                                    gender: 'other',
                                                                                                }}
                                                                                            />
                                                                                        </Action>
                                                                                    ) : null
                                                                                }
                                                                            </ViewerConsumer>
                                                                            {close}
                                                                        </React.Fragment>
                                                                    )}
                                                                </ActionSheet>
                                                                <div
                                                                    className={classnames(styles.footer, {
                                                                        [styles.open]: commentsOpen,
                                                                    })}
                                                                >
                                                                    <Likers
                                                                        id={id}
                                                                        likesCount={likesCount || 0}
                                                                        {...rest}
                                                                    />
                                                                    <LikeUnlike
                                                                        __typename={__typename}
                                                                        id={id}
                                                                        likesCount={likesCount}
                                                                        isLikedByViewer={isLikedByViewer}
                                                                        inFeed={inFeed}
                                                                    />
                                                                    {!isPrArticle ? (
                                                                        <Button onClick={onComments}>
                                                                            {commentsLoading ? (
                                                                                <Spinner
                                                                                    style={{ spinner: styles.spinner }}
                                                                                    mini={true}
                                                                                />
                                                                            ) : (
                                                                                <SVGIcon
                                                                                    style={{ image: styles.icon }}
                                                                                    src={comments}
                                                                                />
                                                                            )}
                                                                            {commentCount ? (
                                                                                <React.Fragment>
                                                                                    <FormattedMessage id='COMMENTS' />
                                                                                    <span
                                                                                        className={styles.commentCount}
                                                                                    >
                                                                                        ({commentCount})
                                                                                    </span>
                                                                                </React.Fragment>
                                                                            ) : (
                                                                                <FormattedMessage id='BUTTON_COMMENT' />
                                                                            )}
                                                                        </Button>
                                                                    ) : null}
                                                                    <Button onClick={this.openMore}>
                                                                        <SVGIcon
                                                                            style={{ image: styles.icon }}
                                                                            src={others}
                                                                        />
                                                                        <FormattedMessage id='BUTTON_LABEL_MORE'>
                                                                            {msg => <span>{msg}</span>}
                                                                        </FormattedMessage>
                                                                    </Button>
                                                                </div>
                                                            </>
                                                        );
                                                    }}
                                                </Mutation>
                                            )}
                                        </SubscribeUnsubscribePost>
                                    )}
                                </ComplainDialog>
                            );
                        }}
                    </Mutation>
                )}
            </AlertContext.Consumer>
        );
    }
}

export default Styled(defaultStyles)(Footer);
